import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
 HiOutlinePlus,
 HiOutlineX,
 HiOutlineExclamation,
 HiOutlineCalendar,
 HiOutlineViewList,
 HiOutlineTrash,
 HiOutlineChevronLeft,
 HiOutlineChevronRight,
} from "react-icons/hi";
import {
 fetchAssessmentYears,
 fetchHolidays,
 fetchHolidaysCalendar,
 fetchHolidayGroupById,
 deleteHoliday,
 saveHolidayCalendar,
 fetchMasterData,
} from "../services/masterDataApi";
import SearchableDropdown, { scopedStatesFetchFn } from "../components/SearchableDropdown";
import Toast from "../components/Toast";
import { useUserRole } from "../hooks/useUserRole";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
 "January","February","March","April","May","June",
 "July","August","September","October","November","December",
];

function formatDate(iso) {
 if (!iso) return "—";
 const [y, m, d] = iso.split("-");
 const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
 return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
}

function Holidays() {
 const { canManageHolidays, role } = useUserRole();

 /* ── views: "list" | "form" | "calendar" — backed by the URL so a refresh
    while editing a calendar reopens the same state/year group instead of
    dropping back to the list. A calendar "group" has no single id, so both
    the state id and assessment year id go in the URL. ── */
 const [searchParams, setSearchParams] = useSearchParams();
 const view = searchParams.get("view") || "list";
 const urlEditId = searchParams.get("edit"); // state id, when editing a group
 const urlAssessmentYearId = searchParams.get("assessmentYearId");
 const setView = useCallback((v, params = {}) => {
  if (v === "list") {
   setSearchParams({});
  } else if (v === "form" && params.edit) {
   setSearchParams({
    view: "form",
    edit: params.edit,
    ...(params.assessmentYearId ? { assessmentYearId: params.assessmentYearId } : {}),
   });
  } else {
   setSearchParams({ view: v });
  }
 }, [setSearchParams]);
 const [allStates, setAllStates] = useState([]);     // listing display (all visible states)
 const [allowedStates, setAllowedStates] = useState([]); // modal dropdown (only writable states)
 const [assessmentYears, setAssessmentYears] = useState([]);
 const [allHolidays, setAllHolidays] = useState([]);
 const [loading, setLoading] = useState(false);
 const [toast, setToast] = useState(null);

 // calendar view
 const now = new Date();
 const [calStateId, setCalStateId] = useState("");
 const [calYearId, setCalYearId] = useState("");
 const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
 const [calYear, setCalYear] = useState(now.getFullYear());
 const [calHolidays, setCalHolidays] = useState([]);

 // form (create/edit)
 const [isEditMode, setIsEditMode] = useState(false);
 const [modalStateId, setModalStateId] = useState("");
 const [modalYearId, setModalYearId] = useState("");
 const [entries, setEntries] = useState([]); // { _key, id?, date, name }
 // Published, not-completed cycle that froze this calendar / any not-completed cycle that
 // selected it (blocks moving or deleting holidays). null = free.
 const [editLockedBy, setEditLockedBy] = useState(null);
 const [editSelectedBy, setEditSelectedBy] = useState(null);
 const [origGroup, setOrigGroup] = useState({ stateId: "", yearId: "" });
 const [saving, setSaving] = useState(false);

 // delete confirm
 const [deleteTarget, setDeleteTarget] = useState(null);

 // ── Load reference data ──
 useEffect(() => {
  fetchAssessmentYears().then((res) => setAssessmentYears(res.data || [])).catch(() => {});
  // The states list is already role-scoped server-side (ADMIN: all, SPL:
  // own states), so it doubles as the modal's writable-states list.
  // PL/PM/PO cannot manage holidays — the modal is hidden for them.
  fetchMasterData("states", { page_size: 100 })
   .then((res) => {
    const sorted = (res.data || []).slice().sort((a, b) => a.name.localeCompare(b.name));
    setAllStates(sorted);
    setAllowedStates(sorted);
   })
   .catch(() => {});
 }, []);

 // ── Load all holidays (paginate through all pages, max 200/page) ──
 const loadAllHolidays = useCallback(async () => {
  setLoading(true);
  try {
   const first = await fetchHolidays({ page: 1, page_size: 200 });
   let all = first.data || [];
   const totalPages = first.total_pages || 1;
   if (totalPages > 1) {
    const rest = await Promise.all(
     Array.from({ length: totalPages - 1 }, (_, i) =>
      fetchHolidays({ page: i + 2, page_size: 200 }).then((r) => r.data || [])
     )
    );
    all = all.concat(...rest);
   }
   setAllHolidays(all);
  } catch {
   setAllHolidays([]);
  } finally {
   setLoading(false);
  }
 }, []);

 useEffect(() => { loadAllHolidays(); }, [loadAllHolidays]);

 // ── Load calendar view holidays ──
 useEffect(() => {
  if (view !== "calendar" || !calStateId || !calYearId) { setCalHolidays([]); return; }
  fetchHolidaysCalendar(calYearId, calMonth, calYear, Number(calStateId))
   .then((res) => setCalHolidays(res.holidays || []))
   .catch(() => setCalHolidays([]));
 }, [view, calStateId, calYearId, calMonth, calYear]);

 const prevMonth = () => { if (calMonth === 1) { setCalMonth(12); setCalYear((y) => y - 1); } else setCalMonth((m) => m - 1); };
 const nextMonth = () => { if (calMonth === 12) { setCalMonth(1); setCalYear((y) => y + 1); } else setCalMonth((m) => m + 1); };

 const daysInMonth = new Date(calYear, calMonth, 0).getDate();
 const firstDayOfWeek = new Date(calYear, calMonth - 1, 1).getDay();
 const calendarDays = [];
 for (let i = 0; i < firstDayOfWeek; i++) calendarDays.push(null);
 for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

 const getHolidaysForDay = (day) => {
  if (!day) return [];
  const dateStr = `${calYear}-${String(calMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return calHolidays.filter((h) => h.date === dateStr);
 };

 // ── Group holidays into calendar rows ──
 const calendarGroups = useMemo(() => {
  const map = new Map();
  for (const h of allHolidays) {
   const key = `${h.state_id}::${h.assessment_year_id}`;
   if (!map.has(key)) {
    map.set(key, { state_id: h.state_id, assessment_year_id: h.assessment_year_id, holidays: [], maxUpdated: "" });
   }
   const g = map.get(key);
   g.holidays.push(h);
   const upd = h.updated_at ? h.updated_at.slice(0, 10) : "";
   if (upd > g.maxUpdated) g.maxUpdated = upd;
  }
  const groups = Array.from(map.values());
  groups.sort((a, b) => {
   const sa = allStates.find((s) => s.id === a.state_id)?.name || "";
   const sb = allStates.find((s) => s.id === b.state_id)?.name || "";
   return sa.localeCompare(sb);
  });
  return groups;
 }, [allHolidays, allStates]);

 // ── Open create modal ──
 const openCreate = () => {
  setIsEditMode(false);
  setModalStateId("");
  setModalYearId("");
  setEditLockedBy(null);
  setEditSelectedBy(null);
  setEntries([]);
  setView("form");
 };

 // ── Open edit form ──
 const openEdit = (group) => {
  // A direct click already has the full group — mark the URL-restore effect
  // as satisfied so it doesn't also redundantly re-fetch this same group.
  hasRestoredFromUrl.current = true;
  setIsEditMode(true);
  setModalStateId(String(group.state_id));
  setModalYearId(String(group.assessment_year_id));
  setOrigGroup({ stateId: String(group.state_id), yearId: String(group.assessment_year_id) });
  setEditLockedBy(group.holidays.find((h) => h.locked_by_cycle)?.locked_by_cycle || null);
  setEditSelectedBy(group.holidays.find((h) => h.selected_by_cycle)?.selected_by_cycle || null);
  setEntries(
   group.holidays
    .slice()
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map((h) => ({ _key: h.id, id: h.id, date: h.date, name: h.name }))
  );
  setView("form", { edit: String(group.state_id), assessmentYearId: String(group.assessment_year_id) });
 };

 // Restore edit view from URL on refresh. A group needs both ids — a
 // malformed/stale URL (edit set but assessmentYearId missing, e.g. a
 // bookmark saved before this param existed) falls back to the list instead
 // of leaving the effect permanently unresolved.
 const hasRestoredFromUrl = useRef(false);
 useEffect(() => {
  if (hasRestoredFromUrl.current) return;
  if (view === "form" && urlEditId && urlAssessmentYearId) {
   hasRestoredFromUrl.current = true;
   fetchHolidayGroupById(urlEditId, urlAssessmentYearId)
    .then((res) => openEdit(res.data))
    .catch(() => setView("list"));
  } else if (view === "form" && !urlEditId) {
   hasRestoredFromUrl.current = true;
   openCreate();
  } else if (view === "form") {
   // edit present but assessmentYearId missing — can't resolve a group
   hasRestoredFromUrl.current = true;
   setView("list");
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [view, urlEditId, urlAssessmentYearId]);

 const addEntryRow = () => {
  setEntries((prev) => [
   ...prev,
   { _key: `new_${prev.length}_${Date.now()}`, id: undefined, date: "", name: "" },
  ]);
 };

 const updateEntry = (key, field, value) => {
  setEntries((prev) => prev.map((e) => e._key === key ? { ...e, [field]: value } : e));
 };

 const removeEntry = (key) => {
  setEntries((prev) => prev.filter((e) => e._key !== key));
 };

 // ── Save the whole calendar in one request: nothing is saved if any entry is wrong ──
 const handleSave = async () => {
  if (!modalStateId || !modalYearId) return;
  setSaving(true);
  try {
   await saveHolidayCalendar({
    original_state_id: isEditMode ? parseInt(origGroup.stateId, 10) : null,
    original_assessment_year_id: isEditMode ? origGroup.yearId : null,
    state_id: parseInt(modalStateId, 10),
    assessment_year_id: modalYearId,
    holidays: validEntries.map((e) => ({ name: e.name, date: e.date })),
   });
   setToast({ type: "success", message: "Calendar saved successfully" });
   setView("list");
   loadAllHolidays();
  } catch (err) {
   // The form keeps the user's edits so they can fix the problem and save again.
   const detail = err.response?.data?.detail;
   setToast({ type: "error", message: typeof detail === "string" ? detail : "Failed to save calendar" });
  } finally {
   setSaving(false);
  }
 };

 // ── Delete entire calendar ──
 const handleDeleteCalendar = async () => {
  if (!deleteTarget) return;
  try {
   const group = calendarGroups.find(
    (g) => g.state_id === deleteTarget.state_id && String(g.assessment_year_id) === String(deleteTarget.assessment_year_id)
   );
   if (group) await Promise.all(group.holidays.map((h) => deleteHoliday(h.id)));
   setToast({ type: "success", message: "Calendar deleted" });
   setDeleteTarget(null);
   setView("list");
   loadAllHolidays();
  } catch {
   setToast({ type: "error", message: "Failed to delete calendar" });
  }
 };

 const modalStateName = allStates.find((s) => String(s.id) === modalStateId)?.name || "";
 const modalYearName = assessmentYears.find((y) => String(y.id) === modalYearId)?.name || "";
 const validEntries = entries.filter((e) => e.date && e.name);
 const isLocked = isEditMode && !!editLockedBy;
 const isPinned = isEditMode && !!editSelectedBy;

 return (
  <div className="p-6">
   {toast && <Toast {...toast} onClose={() => setToast(null)} />}

   {/* ── Header ── */}
   <div className="flex items-center justify-between mb-6">
    <div>
     <h1 className="text-xl font-bold text-[#24292f]">Holidays</h1>
     <p className="text-sm text-[#57606a] mt-1">Manage holiday calendars by state and assessment year</p>
    </div>
    <div className="flex items-center gap-3">
     <div className="flex bg-[#f3f4f6] rounded-lg p-0.5">
      <button
       onClick={() => setView("list")}
       className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${view === "list" ? "bg-white text-[#0078d4] shadow-sm" : "text-[#57606a]"}`}
      >
       <HiOutlineViewList className="text-sm" /> List
      </button>
      <button
       onClick={() => setView("calendar")}
       className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${view === "calendar" ? "bg-white text-[#0078d4] shadow-sm" : "text-[#57606a]"}`}
      >
       <HiOutlineCalendar className="text-sm" /> Calendar
      </button>
     </div>
     {canManageHolidays && view === "list" && (
      <button
       onClick={openCreate}
       className="flex items-center gap-1.5 px-4 py-2 bg-[#0078d4] text-white text-xs font-medium rounded-lg hover:bg-[#106ebe] transition-all"
      >
       <HiOutlinePlus className="text-sm" /> Add Calendar
      </button>
     )}
    </div>
   </div>

   {/* ═══ LIST VIEW ═══ */}
   {view === "list" && <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
    <table className="w-full text-sm">
     <thead>
      <tr className="border-b border-[#e5e7eb] bg-[#f6f8fa]">
       <th className="text-left px-4 py-3 text-xs font-semibold text-[#8b949e] uppercase tracking-wide">State</th>
       <th className="text-left px-4 py-3 text-xs font-semibold text-[#8b949e] uppercase tracking-wide">Assessment Year</th>
       <th className="text-left px-4 py-3 text-xs font-semibold text-[#8b949e] uppercase tracking-wide">Holidays</th>
       <th className="text-left px-4 py-3 text-xs font-semibold text-[#8b949e] uppercase tracking-wide">Last Updated</th>
      </tr>
     </thead>
     <tbody>
      {loading ? (
       <tr>
        <td colSpan={4} className="text-center py-14 text-[#8b949e] text-sm">
         Loading…
        </td>
       </tr>
      ) : calendarGroups.length === 0 ? (
       <tr>
        <td colSpan={4} className="text-center py-14">
         <HiOutlineCalendar className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
         <p className="text-sm text-[#8b949e]">No holiday calendars found.</p>
         {canManageHolidays && (
          <button onClick={openCreate} className="mt-3 text-xs text-[#0078d4] hover:underline">
           Add your first calendar
          </button>
         )}
        </td>
       </tr>
      ) : (
       calendarGroups.map((g) => {
        const stateName = allStates.find((s) => s.id === g.state_id)?.name || (g.state_id ? `State #${g.state_id}` : "Unknown State");
        const year = assessmentYears.find((y) => String(y.id) === String(g.assessment_year_id));
        const yearName = year?.name || "—";
        return (
         <tr
          key={`${g.state_id}-${g.assessment_year_id}`}
          onClick={() => canManageHolidays && openEdit(g)}
          className={`border-b border-[#f0f0f0] transition-colors ${canManageHolidays ? "hover:bg-[#f0f6ff] cursor-pointer" : "hover:bg-[#f9fafb]"}`}
         >
          <td className="px-4 py-3.5 font-semibold text-[#24292f]">{stateName}</td>
          <td className="px-4 py-3.5">
           <span className="inline-block px-2.5 py-0.5 text-xs font-medium bg-[#f3f4f6] text-[#57606a] rounded-full border border-[#e5e7eb]">
            {yearName}
           </span>
          </td>
          <td className="px-4 py-3.5">
           <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium bg-[#eff6ff] text-[#0078d4] rounded-full">
            <HiOutlineCalendar className="text-[11px]" />
            {g.holidays.length} {g.holidays.length === 1 ? "day" : "days"}
           </span>
          </td>
          <td className="px-4 py-3.5 text-[#57606a] text-xs">{formatDate(g.maxUpdated)}</td>
         </tr>
        );
       })
      )}
     </tbody>
    </table>
   </div>}

   {/* ═══ CALENDAR VIEW ═══ */}
   {view === "calendar" && (
    <div className="space-y-4">
     {/* state + year filter */}
     <div className="bg-white rounded-xl border border-[#e5e7eb] px-5 py-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
       <div>
        <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">State</label>
        <SearchableDropdown
         value={String(calStateId || "")}
         onChange={(id) => setCalStateId(id)}
         placeholder="Select State"
         fetchFn={(search, page = 1) => fetchMasterData("states", { page, page_size: 20, search })}
         cacheNamespace="holiday_filter_state_id"
         variant="report"
        />
       </div>
       <div>
        <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Assessment Year</label>
        <select
         value={calYearId}
         onChange={(e) => setCalYearId(e.target.value)}
         disabled={!calStateId}
         className="w-full border border-[#d0d7de] rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4] disabled:opacity-50"
        >
         <option value="">Select Year</option>
         {assessmentYears.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
        </select>
       </div>
      </div>
     </div>

     {(!calStateId || !calYearId) ? (
      <div className="bg-white rounded-xl border border-[#e5e7eb] p-12 text-center">
       <HiOutlineCalendar className="mx-auto text-4xl text-[#d0d5dd] mb-3" />
       <p className="text-sm text-[#8b949e]">Select a state and assessment year to view the calendar.</p>
      </div>
     ) : (
      <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
       <div className="flex items-center justify-between px-5 py-3 border-b border-[#e5e7eb] bg-[#f6f8fa]">
        <button onClick={prevMonth} className="p-1.5 hover:bg-[#e5e7eb] rounded-md transition-all">
         <HiOutlineChevronLeft className="text-lg text-[#24292f]" />
        </button>
        <h2 className="text-base font-semibold text-[#24292f]">
         {MONTH_NAMES[calMonth - 1]} {calYear}
        </h2>
        <button onClick={nextMonth} className="p-1.5 hover:bg-[#e5e7eb] rounded-md transition-all">
         <HiOutlineChevronRight className="text-lg text-[#24292f]" />
        </button>
       </div>
       <div className="grid grid-cols-7 border-b border-[#e5e7eb]">
        {DAYS.map((d) => (
         <div key={d} className="text-center py-2 text-xs font-semibold text-[#8b949e] uppercase tracking-wider">{d}</div>
        ))}
       </div>
       <div className="grid grid-cols-7">
        {calendarDays.map((day, idx) => {
         const dayHolidays = getHolidaysForDay(day);
         const isToday = day && calMonth === now.getMonth() + 1 && calYear === now.getFullYear() && day === now.getDate();
         return (
          <div key={idx} className={`min-h-[100px] border-b border-r border-[#f0f0f0] p-1.5 ${day ? "hover:bg-[#f6f8fa]" : "bg-[#fafafa]"}`}>
           {Boolean(day) && (
            <>
             <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? "bg-[#0078d4] text-white" : "text-[#57606a]"}`}>
              {day}
             </div>
             {dayHolidays.map((h, i) => (
              <div key={i} className="text-[11px] px-1.5 py-0.5 mb-0.5 rounded truncate font-medium bg-blue-100 text-blue-700" title={h.name}>
               {h.name}
              </div>
             ))}
            </>
           )}
          </div>
         );
        })}
       </div>
      </div>
     )}
    </div>
   )}

   {/* ═══ ADD / EDIT FORM ═══ */}
   {view === "form" && (
    <div className="bg-white rounded-xl border border-[#e5e7eb] w-full">

      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b border-[#e5e7eb] shrink-0">
       <div className="flex items-start gap-3">
        <button
         onClick={() => setView("list")}
         className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer"
         title="Back"
        >
         <HiOutlineChevronLeft className="text-lg" />
        </button>
        <div>
         <h3 className="text-base font-semibold text-[#24292f]">
          {isEditMode ? `Edit — ${modalStateName} ${modalYearName}` : "Add Holiday Calendar"}
         </h3>
         <p className="text-xs text-[#8b949e] mt-0.5">
          {isEditMode ? "Modify the calendar details below" : "Set up a new state holiday calendar"}
         </p>
        </div>
       </div>
       {isEditMode && !isPinned && (
        <div className="flex items-center gap-1.5 mt-0.5">
         <button
          onClick={() => {
           // The original group, not the (possibly edited) State/Year fields.
           setDeleteTarget({
            state_id: parseInt(origGroup.stateId, 10),
            assessment_year_id: origGroup.yearId,
            stateName: allStates.find((s) => String(s.id) === origGroup.stateId)?.name || "",
            yearName: assessmentYears.find((y) => String(y.id) === origGroup.yearId)?.name || "",
            count: entries.length,
           });
          }}
          className="p-1.5 text-[#8b949e] hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
          title="Delete calendar"
         >
          <HiOutlineTrash className="text-base" />
         </button>
        </div>
       )}
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">

       {/* State + Year */}
       <div className="grid grid-cols-2 gap-4">
        <div>
         <label className="block text-xs font-medium text-[#24292f] mb-1">State *</label>
         <SearchableDropdown
          value={String(modalStateId || "")}
          onChange={(id) => setModalStateId(id)}
          placeholder="Select state"
          fetchFn={scopedStatesFetchFn(role === "ADMIN", allowedStates)}
          cacheNamespace="holiday_modal_state_id"
          disabled={isPinned}
          variant="form"
         />
        </div>
        <div>
         <label className="block text-xs font-medium text-[#24292f] mb-1">Assessment Year *</label>
         <select
          value={modalYearId}
          onChange={(e) => setModalYearId(e.target.value)}
          disabled={isPinned}
          className="w-full border border-[#d0d7de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4] disabled:bg-[#f6f8fa] disabled:text-[#57606a]"
         >
          <option value="">Select year</option>
          {assessmentYears.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
         </select>
        </div>
       </div>
       {isPinned && (
        <p className="text-xs text-[#9a6700] bg-[#fff8c5] border border-[#d4a72c]/40 rounded-lg px-3 py-2">
         {isLocked
          ? `This calendar is used in cycle '${editLockedBy}'. Unpublish the cycle to edit it.`
          : `This calendar is used in cycle '${editSelectedBy}'. Remove the calendar from the cycle to change its state or delete holidays.`}
        </p>
       )}

       {/* Holiday entries */}
       <div>
        <p className="text-[10.5px] font-semibold text-[#8b949e] uppercase tracking-wide mb-3">Holiday Days</p>
        <div className="flex items-center justify-between mb-2">
         <span className="text-sm font-medium text-[#24292f]">Holiday Entries</span>
         {validEntries.length > 0 && (
          <span className="text-xs font-medium text-[#0078d4]">
           {validEntries.length} {validEntries.length === 1 ? "day" : "days"} added
          </span>
         )}
        </div>

        {entries.length === 0 && (
         <p className="text-xs text-[#8b949e] text-center py-5 border border-dashed border-[#d0d7de] rounded-lg mb-2">
          No holidays yet — click below to add one
         </p>
        )}

        <div className="space-y-2 mb-3">
         {entries.map((e) => (
          <div key={e._key} className="flex items-center gap-2">
           <input
            type="date"
            value={e.date}
            onChange={(ev) => updateEntry(e._key, "date", ev.target.value)}
            disabled={isLocked}
            ref={(el) => { if (el && !el._noScroll) { el._noScroll = true; el.addEventListener('wheel', (ev) => ev.preventDefault(), { passive: false }); } }}
            className="w-36 shrink-0 border border-[#d0d7de] rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4] disabled:bg-[#f6f8fa] disabled:text-[#57606a]"
           />
           <input
            type="text"
            placeholder="Holiday name"
            value={e.name}
            onChange={(ev) => updateEntry(e._key, "name", ev.target.value)}
            disabled={isLocked}
            className="flex-1 border border-[#d0d7de] rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4] disabled:bg-[#f6f8fa] disabled:text-[#57606a]"
           />
           {!isLocked && !(isPinned && e.id) && <button
            onClick={() => removeEntry(e._key)}
            className="p-1 text-[#8b949e] hover:text-red-500 hover:bg-red-50 rounded-md transition-all shrink-0"
           >
            <HiOutlineX className="text-sm" />
           </button>}
          </div>
         ))}
        </div>

        {!isLocked && <button
         onClick={addEntryRow}
         className="w-full py-2 text-xs font-medium text-[#0078d4] border border-dashed border-[#0078d4]/40 rounded-lg hover:bg-[#eff6ff] transition-all"
        >
         + Add Holiday
        </button>}
       </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-xl shrink-0">
       <button
        onClick={() => setView("list")}
        className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6] transition-all"
       >
        Cancel
       </button>
       <button
        onClick={handleSave}
        disabled={isLocked || saving || !modalStateId || !modalYearId || validEntries.length === 0}
        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#0078d4] rounded-lg hover:bg-[#106ebe] disabled:opacity-40 transition-all"
       >
        {saving ? "Saving…" : "Save Calendar"}
       </button>
      </div>
    </div>
   )}

   {/* ═══ DELETE CONFIRM ═══ */}
   {deleteTarget && (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
     <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
       <HiOutlineExclamation className="text-2xl text-red-600" />
      </div>
      <h3 className="text-base font-semibold text-[#24292f] mb-1">Delete Calendar?</h3>
      <p className="text-sm text-[#57606a] mb-4">
       This will permanently delete all {deleteTarget.count} {deleteTarget.count === 1 ? "holiday" : "holidays"} for{" "}
       <strong>{deleteTarget.stateName}</strong> ({deleteTarget.yearName}).
      </p>
      <div className="flex justify-center gap-2">
       <button
        onClick={() => setDeleteTarget(null)}
        className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6] transition-all"
       >
        Cancel
       </button>
       <button
        onClick={handleDeleteCalendar}
        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-all"
       >
        Delete All
       </button>
      </div>
     </div>
    </div>
   )}
  </div>
 );
}

export default Holidays;
